// src/components/reader/sectionDrills.js
//
// The one decision behind a section's "Drill It" block (settext-section-drills): given
// the reader's ALREADY-GATED quiz and flashcard items, which of them belong to THIS
// section, and is there anything runnable to show? Pure and shared by every reader, so
// the set-text reader and the grouped readers cannot drift.
//
// Nothing here widens access: callers pass the item lists their own free/paid gate
// already produced, and the knownItemIds gate (the sections the user can SEE) is the
// same one inline audio uses -- an item linked to an invisible section stays out.
import { drillQuizzesForSection, drillFlashcardsForSection, toQuizQuestions } from '../../pages/setTextReaderGate.js';

export function sectionDrillsFor(quizItems, flashcardItems, knownItemIds, sectionId) {
    // Arrays only: the bucket helpers iterate their input, and a stray non-array (a
    // half-loaded pool) must degrade to "no drill", not throw inside the lesson.
    const q = Array.isArray(quizItems) ? quizItems : [];
    const f = Array.isArray(flashcardItems) ? flashcardItems : [];
    const quizzes = drillQuizzesForSection(q, knownItemIds, sectionId);
    const flashcards = drillFlashcardsForSection(f, knownItemIds, sectionId);
    // Gate on RUNNABLE questions, not raw linked items: toQuizQuestions drops malformed
    // records, and a section whose linked quizzes ALL fail mapping must render exactly
    // as if it had none -- never a "Drill It" header over an empty-state message.
    const runnable = toQuizQuestions(quizzes).length > 0;
    return { quizzes, flashcards, runnable, visible: !!sectionId && (runnable || flashcards.length > 0) };
}

// The visibility set for DRILLS in the set-text reader (guide-section-drills). That reader's
// knownTextIds holds set_text ids only -- and the essay-structure GUIDE is a lit_section,
// so quizzes linked to it were partitioned to pack-level and never drilled. This adds the
// guide's id for the drill mounts ONLY. knownTextIds itself is untouched: it also feeds the
// audio, essay and pooled-practice partitions, and moving the guide into those would
// silently re-bucket audio and essays. No guide -> the same members as knownTextIds.
export function withGuideId(knownIds, guide) {
    const out = new Set(knownIds instanceof Set ? knownIds : (knownIds || []));
    if (guide && typeof guide.id === 'string' && guide.id) out.add(guide.id);
    return out;
}

// Plural variant for the reader; leave the shared text/audio ID set unchanged.
export function withGuideIds(knownIds, guides) {
    return (guides || []).reduce((ids, guide) => withGuideId(ids, guide), new Set(knownIds || []));
}
